import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBusinessUnit } from '../context/BusinessUnitContext';
import { useGlobalSettings } from '../context/GlobalSettingsContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { LayoutDashboard, Users, DollarSign, Activity, Settings, Building2, Building, BarChart3, LogOut, FileUp, UserCheck, UserPlus, Mail, CheckCircle2, Loader2, Menu, X, ChevronDown, User, Trash2, Bell, XCircle, Phone, Calendar, Sun, Moon, AlertTriangle, Clock, HelpCircle, BookOpen, MessageSquare, FileText, ChevronRight, Search, PanelLeftClose, PanelLeft, Command, Keyboard, ListTodo, Archive } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { formatDate } from '../lib/dateFormat';

const Layout = () => {
  const { user, logout } = useAuth();
  const { getCurrentLogo, getCurrentName } = useBusinessUnit();
  const { dateFormat, timezone, branding, theme, toggleTheme } = useGlobalSettings();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Microsoft account connection state
  const [msConnected, setMsConnected] = useState(false);
  const [connectingMs, setConnectingMs] = useState(false);
  
  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Notification state
  const [notifications, setNotifications] = useState({ count: 0, callbacks: [] });
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  
  // Stale deals state
  const [staleDeals, setStaleDeals] = useState({ count: 0, deals: [] });
  
  // Theme state (local state for immediate UI response)
  const [currentTheme, setCurrentTheme] = useState(() => localStorage.getItem('crm-theme') || 'light');
  
  // Help guide state
  const [helpOpen, setHelpOpen] = useState(false);
  const [expandedHelpSection, setExpandedHelpSection] = useState(null);

  // Google connection state
  const [googleConnected, setGoogleConnected] = useState(false);
  const [connectingGoogle, setConnectingGoogle] = useState(false);

  // Sidebar collapsed state - persist in localStorage
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('crm-sidebar-collapsed');
    return saved === 'true';
  });

  // Quick search modal state
  const [quickSearchOpen, setQuickSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ deals: [], contacts: [], companies: [] });
  const [searchLoading, setSearchLoading] = useState(false);

  // Toggle sidebar and persist
  const toggleSidebar = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('crm-sidebar-collapsed', String(newState));
  };

  // Quick search function
  const performQuickSearch = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults({ deals: [], contacts: [], companies: [] });
      return;
    }
    
    setSearchLoading(true);
    try {
      const [dealsRes, contactsRes, companiesRes] = await Promise.all([
        api.get('/deals', { params: { search: query } }),
        api.get('/contacts', { params: { search: query } }),
        api.get('/companies', { params: { search: query } })
      ]);
      
      setSearchResults({
        deals: (dealsRes.data || []).slice(0, 5),
        contacts: (contactsRes.data || []).slice(0, 5),
        companies: (companiesRes.data || []).slice(0, 5)
      });
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore if user is typing in an input/textarea
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        // Allow Escape to close modals even when in input
        if (e.key === 'Escape') {
          setQuickSearchOpen(false);
          setHelpOpen(false);
        }
        return;
      }

      // Ctrl/Cmd + B - Toggle sidebar
      if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
        e.preventDefault();
        toggleSidebar();
      }
      
      // Ctrl/Cmd + K - Quick search
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setQuickSearchOpen(true);
        setSearchQuery('');
        setSearchResults({ deals: [], contacts: [], companies: [] });
      }
      
      // Ctrl/Cmd + / - Toggle help
      if ((e.ctrlKey || e.metaKey) && e.key === '/') {
        e.preventDefault();
        setHelpOpen(!helpOpen);
      }
      
      // Escape - Close modals
      if (e.key === 'Escape') {
        setQuickSearchOpen(false);
        setHelpOpen(false);
      }

      // G then D - Go to Dashboard
      // G then P - Go to Pipeline
      // G then C - Go to Contacts
      // G then S - Go to Settings
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [helpOpen, sidebarCollapsed]);

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const response = await api.get('/notifications/callbacks');
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };
  
  // Fetch stale deals
  const fetchStaleDeals = async () => {
    try {
      const response = await api.get('/deals/stale');
      setStaleDeals(response.data);
    } catch (error) {
      console.error('Failed to fetch stale deals:', error);
    }
  };

  useEffect(() => {
    fetchNotifications();
    fetchStaleDeals();
    // Refresh notifications every 5 minutes
    const interval = setInterval(() => {
      fetchNotifications();
      fetchStaleDeals();
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Apply theme on mount based on localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem('crm-theme') || 'light';
    const html = document.documentElement;
    const body = document.body;
    
    if (savedTheme === 'dark') {
      html.classList.add('dark');
      body.classList.add('dark');
      html.setAttribute('data-theme', 'dark');
    } else {
      html.classList.remove('dark');
      body.classList.remove('dark');
      html.setAttribute('data-theme', 'light');
    }
    
    setCurrentTheme(savedTheme);
  }, []);

  useEffect(() => {
    // Integration status is static for a session: check once, not on every navigation
    const checkMsStatus = async () => {
      try {
        const response = await api.get('/integrations/outlook/status');
        setMsConnected(response.data.connected);
      } catch (error) {
        console.error('Failed to check MS status:', error);
      }
    };
    checkMsStatus();
    
    const checkGoogleStatus = async () => {
      try {
        const response = await api.get('/integrations/google/status');
        setGoogleConnected(response.data.connected);
      } catch (error) {
        console.error('Failed to check Google status:', error);
      }
    };
    checkGoogleStatus();
  }, []);

  useEffect(() => {
    // Check for OAuth callback on any page
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('outlook_connected') === 'true') {
      toast.success('Microsoft account connected successfully!');
      window.history.replaceState({}, '', location.pathname);
      setMsConnected(true);
    } else if (urlParams.get('google_connected') === 'true') {
      toast.success('Google account connected successfully!');
      window.history.replaceState({}, '', location.pathname);
      setGoogleConnected(true);
    } else if (urlParams.get('error')) {
      toast.error(`Failed to connect: ${urlParams.get('error')}`);
      window.history.replaceState({}, '', location.pathname);
    }
  }, [location.pathname]);
  
  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleConnectMicrosoft = async () => {
    setConnectingMs(true);
    try {
      const response = await api.get('/integrations/outlook/auth-url');
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Microsoft integration not configured. Please contact admin.');
      setConnectingMs(false);
    }
  };

  const handleConnectGoogle = async () => {
    setConnectingGoogle(true);
    try {
      const response = await api.get('/integrations/google/auth-url');
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Google integration not configured. Please contact admin.');
      setConnectingGoogle(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/lead-finder', label: 'Lead Finder', icon: Search },
    { path: '/contacts', label: 'Customers', icon: Building2 },
    { path: '/leads', label: 'Capture Lead', icon: UserPlus },
    { path: '/pipeline', label: 'Pipeline', icon: DollarSign },
    { path: '/next-steps', label: 'Next Steps', icon: ListTodo },
    { path: '/disqualified-leads', label: 'Disqualified', icon: XCircle },
    { path: '/archived', label: 'Archived', icon: Archive },
    { path: '/activities', label: 'Activities', icon: Activity },
    ...(user?.role === 'admin' || user?.role === 'bu_manager' ? [{ path: '/import', label: 'Import Data', icon: FileUp }] : []),
    ...(user?.role === 'admin' || user?.role === 'bu_manager' ? [{ path: '/reports', label: 'Reports', icon: BarChart3 }] : []),
    ...(user?.role === 'admin' || user?.role === 'bu_manager' ? [{ path: '/lead-generator-report', label: 'Lead Generators', icon: UserPlus }] : []),
    { path: '/user-performance', label: 'User Performance', icon: UserCheck },
    ...(user?.role === 'admin' || user?.role === 'bu_manager' ? [{ path: '/deleted-items', label: 'Deleted Items', icon: Trash2 }] : []),
    ...(user?.role === 'admin' ? [{ path: '/settings', label: 'Settings', icon: Settings }] : []),
  ];

  // Desktop Sidebar Navigation Content (without logo - logo is in header now)
  const SidebarNav = ({ collapsed = false }) => {
    const navTextColor = branding?.navTextColor || '#ffffff';
    
    return (
      <>
        <nav className="flex-1 p-3 md:p-4 space-y-1 overflow-y-auto pt-6" style={{ backgroundColor: branding?.navColor || '#1e293b' }}>
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                title={collapsed ? item.label : undefined}
                className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'} px-3 md:px-4 py-2.5 md:py-3 rounded-md text-sm font-medium transition-colors`}
                style={{
                  color: navTextColor,
                  opacity: isActive ? 1 : 0.8,
                  backgroundColor: isActive ? 'rgba(255,255,255,0.2)' : 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
                    e.currentTarget.style.opacity = '1';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.opacity = '0.8';
                  }
                }}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!collapsed && <span className="truncate">{item.label}</span>}
              </Link>
            );
          })}
        </nav>
        {/* Collapse Toggle Button at bottom */}
        <div className="p-2" style={{ backgroundColor: branding?.navColor || '#1e293b', borderTop: `1px solid ${navTextColor}20` }}>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleSidebar}
            className={`w-full ${collapsed ? 'justify-center' : 'justify-start'}`}
            style={{ color: navTextColor, opacity: 0.7 }}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            data-testid="sidebar-toggle"
          >
            {collapsed ? (
              <PanelLeft className="w-5 h-5" />
            ) : (
              <>
                <PanelLeftClose className="w-5 h-5 mr-2" />
                <span className="text-xs">Collapse</span>
              </>
            )}
          </Button>
        </div>
      </>
    );
  };

  // Mobile Navigation Content (includes user section at bottom)
  const MobileNavContent = () => (
    <>
      <SidebarNav />
      <div className="p-3 md:p-4 border-t border-border">
        <div className="flex items-center gap-3 px-3 md:px-4 py-2 md:py-3 mb-2">
          <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-secondary flex items-center justify-center font-heading font-bold text-sm">
            {user?.first_name?.[0]}{user?.last_name?.[0]}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.first_name} {user?.last_name}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
        </div>
        {/* Microsoft Account Connection */}
        <Button
          onClick={handleConnectMicrosoft}
          variant={msConnected ? "outline" : "default"}
          className={`w-full justify-start rounded-md mb-2 text-sm ${msConnected ? 'border-green-500 text-green-700 hover:bg-green-50' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
          disabled={connectingMs || msConnected}
          data-testid="connect-microsoft-button-mobile"
        >
          {connectingMs ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : msConnected ? (
            <CheckCircle2 className="w-4 h-4 mr-2 text-green-600" />
          ) : (
            <Mail className="w-4 h-4 mr-2" />
          )}
          <span className="truncate">{msConnected ? 'Microsoft Connected' : 'Connect Microsoft'}</span>
        </Button>
        <Button
          onClick={handleLogout}
          variant="outline"
          className="w-full justify-start rounded-md text-sm"
          data-testid="logout-button-mobile"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Logout
        </Button>
      </div>
    </>
  );

  // User Profile Dropdown (for top-right)
  // Notification Bell Component
  const NotificationBell = ({ textColor }) => (
    <Popover open={notificationsOpen} onOpenChange={setNotificationsOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="relative p-2 hover:bg-white/10" style={{ color: textColor || '#ffffff' }} data-testid="notification-bell">
          <Bell className="w-5 h-5" />
          {notifications.count > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse">
              {notifications.count > 9 ? '9+' : notifications.count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="p-3 border-b border-border bg-secondary/50">
          <h3 className="font-heading font-bold text-sm flex items-center gap-2">
            <Bell className="w-4 h-4" />
            Callback Reminders
          </h3>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {notifications.callbacks.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground text-sm">
              No callbacks due
            </div>
          ) : (
            notifications.callbacks.map((callback) => (
              <div 
                key={callback.id} 
                className="p-3 border-b border-border hover:bg-secondary/30 cursor-pointer"
                onClick={() => {
                  navigate('/disqualified-leads');
                  setNotificationsOpen(false);
                }}
              >
                <div className="font-medium text-sm">{callback.title}</div>
                <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                  <Building2 className="w-3 h-3" />
                  {callback.company_name}
                </div>
                <div className="flex items-center justify-between mt-2">
                  <div className="text-xs text-red-600 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatDate(callback.callback_date, dateFormat, timezone)}
                  </div>
                  {callback.contact_phone && (
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      {callback.contact_phone}
                    </div>
                  )}
                </div>
                <div className="text-xs mt-1 text-muted-foreground line-clamp-2">
                  {callback.disqualified_reason}
                </div>
              </div>
            ))
          )}
        </div>
        {notifications.count > 0 && (
          <div className="p-2 border-t border-border">
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full text-xs"
              onClick={() => {
                navigate('/disqualified-leads');
                setNotificationsOpen(false);
              }}
            >
              View All Disqualified Leads
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );

  // Stale Deals Alert Bell
  // Stale Deals Bell - Self-contained component with its own open state
  const StaleDealsBell = ({ position = 'desktop', headerTextColor }) => {
    const [isOpen, setIsOpen] = useState(false);
    const textColor = headerTextColor || '#ffffff';
    
    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" className="relative p-2 hover:bg-white/10" style={{ color: textColor }} data-testid={`stale-deals-bell-${position}`}>
            <Clock className="w-5 h-5" />
            {staleDeals.count > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 text-white text-xs rounded-full flex items-center justify-center">
                {staleDeals.count > 9 ? '9+' : staleDeals.count}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-0 z-[100]">
          <div className="p-3 border-b border-border bg-amber-50 dark:bg-amber-900/20">
            <h3 className="font-heading font-bold text-sm flex items-center gap-2 text-amber-800 dark:text-amber-200">
              <AlertTriangle className="w-4 h-4" />
              Stale Pipeline Alerts
            </h3>
            <p className="text-xs text-amber-600 dark:text-amber-300 mt-1">Deals not updated in 7+ days</p>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {staleDeals.deals.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground text-sm">
                No stale deals - your pipeline is active!
              </div>
            ) : (
              staleDeals.deals.map((deal) => (
                <div 
                  key={deal.id} 
                  className="p-3 border-b border-border hover:bg-secondary/30 cursor-pointer"
                  onClick={() => {
                    navigate('/pipeline');
                    setIsOpen(false);
                  }}
                >
                  <div className="font-medium text-sm">{deal.title}</div>
                  <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                    <Building2 className="w-3 h-3" />
                    {deal.company_name || 'Unknown Company'}
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {deal.days_stale} days since update
                    </div>
                    <span className="text-xs bg-secondary px-2 py-0.5 rounded capitalize">
                      {deal.stage?.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
          {staleDeals.count > 0 && (
            <div className="p-2 border-t border-border">
              <Button 
                variant="ghost" 
                size="sm" 
                className="w-full text-xs"
                onClick={() => {
                  navigate('/pipeline');
                  setIsOpen(false);
                }}
              >
                View Pipeline
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    );
  };

  // Theme Toggle Button with direct DOM manipulation
  const handleThemeToggle = () => {
    const html = document.documentElement;
    const body = document.body;
    const newTheme = currentTheme === 'light' ? 'dark' : 'light';
    
    if (newTheme === 'dark') {
      html.classList.add('dark');
      body.classList.add('dark');
      html.setAttribute('data-theme', 'dark');
    } else {
      html.classList.remove('dark');
      body.classList.remove('dark');
      html.setAttribute('data-theme', 'light');
    }
    
    localStorage.setItem('crm-theme', newTheme);
    setCurrentTheme(newTheme);
    
    // Force re-render by updating global state too
    if (toggleTheme) toggleTheme();
  };
  
  const ThemeToggle = ({ textColor }) => (
    <Button 
      variant="ghost" 
      size="sm" 
      className="p-2 hover:bg-white/10" 
      style={{ color: textColor || '#ffffff' }}
      onClick={handleThemeToggle}
      data-testid="theme-toggle"
    >
      {currentTheme === 'dark' ? (
        <Sun className="w-5 h-5" />
      ) : (
        <Moon className="w-5 h-5" />
      )}
    </Button>
  );

  const UserProfileDropdown = ({ textColor }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-white/10" style={{ color: textColor || '#ffffff' }} data-testid="user-profile-dropdown">
          <div className="w-8 h-8 rounded-full flex items-center justify-center font-heading font-bold text-sm" style={{ backgroundColor: `${textColor || '#ffffff'}20`, color: textColor || '#ffffff' }}>
            {user?.first_name?.[0]}{user?.last_name?.[0]}
          </div>
          <div className="hidden md:block text-left">
            <p className="text-sm font-medium" style={{ color: textColor || '#ffffff' }}>{user?.first_name} {user?.last_name}</p>
            <p className="text-xs" style={{ color: textColor || '#ffffff', opacity: 0.7 }}>{user?.role}</p>
          </div>
          <ChevronDown className="w-4 h-4" style={{ color: textColor || '#ffffff', opacity: 0.7 }} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col">
            <span className="font-medium">{user?.first_name} {user?.last_name}</span>
            <span className="text-xs text-muted-foreground font-normal">{user?.email}</span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleConnectMicrosoft}
          disabled={connectingMs || msConnected}
          className={msConnected ? 'text-green-700' : ''}
          data-testid="connect-microsoft-button"
        >
          {connectingMs ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : msConnected ? (
            <CheckCircle2 className="w-4 h-4 mr-2 text-green-600" />
          ) : (
            <Mail className="w-4 h-4 mr-2" />
          )}
          {msConnected ? 'Microsoft Connected' : 'Connect Microsoft'}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={handleConnectGoogle}
          disabled={connectingGoogle || googleConnected}
          className={googleConnected ? 'text-green-700' : ''}
          data-testid="connect-google-button"
        >
          {connectingGoogle ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : googleConnected ? (
            <CheckCircle2 className="w-4 h-4 mr-2 text-green-600" />
          ) : (
            <Calendar className="w-4 h-4 mr-2" />
          )}
          {googleConnected ? 'Google Connected' : 'Connect Google'}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-menu-item">
          <Settings className="w-4 h-4 mr-2" />
          Settings
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-button">
          <LogOut className="w-4 h-4 mr-2" />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Quick Search Modal (Ctrl+K) */}
      <Dialog open={quickSearchOpen} onOpenChange={setQuickSearchOpen}>
        <DialogContent className="sm:max-w-[550px] p-0 gap-0">
          <div className="flex items-center border-b px-3">
            <Search className="w-4 h-4 text-muted-foreground mr-2" />
            <Input
              placeholder="Search deals, contacts, customers..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                performQuickSearch(e.target.value);
              }}
              className="border-0 focus-visible:ring-0 h-12"
              autoFocus
            />
          </div>
          <div className="max-h-[400px] overflow-y-auto p-2">
            {searchLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : searchQuery.length < 2 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                <Keyboard className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>Type at least 2 characters to search</p>
                <div className="mt-4 space-y-1 text-xs">
                  <p><kbd className="px-1.5 py-0.5 bg-muted rounded">Ctrl+B</kbd> Toggle sidebar</p>
                  <p><kbd className="px-1.5 py-0.5 bg-muted rounded">Ctrl+/</kbd> Open help guide</p>
                </div>
              </div>
            ) : (
              <>
                {/* Deals Results */}
                {searchResults.deals.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs font-medium text-muted-foreground px-2 mb-1">Deals</p>
                    {searchResults.deals.map((deal) => (
                      <div
                        key={deal.id}
                        className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-secondary cursor-pointer"
                        onClick={() => {
                          navigate('/pipeline');
                          setQuickSearchOpen(false);
                        }}
                      >
                        <DollarSign className="w-4 h-4 text-green-600" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{deal.title}</p>
                          <p className="text-xs text-muted-foreground truncate">{deal.stage}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Contacts Results */}
                {searchResults.contacts.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs font-medium text-muted-foreground px-2 mb-1">Contacts</p>
                    {searchResults.contacts.map((contact) => (
                      <div
                        key={contact.id}
                        className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-secondary cursor-pointer"
                        onClick={() => {
                          navigate('/contacts');
                          setQuickSearchOpen(false);
                        }}
                      >
                        <User className="w-4 h-4 text-blue-600" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{contact.first_name} {contact.last_name}</p>
                          <p className="text-xs text-muted-foreground truncate">{contact.company}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Customers Results */}
                {searchResults.companies.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs font-medium text-muted-foreground px-2 mb-1">Customers</p>
                    {searchResults.companies.map((company) => (
                      <div
                        key={company.id || company.name}
                        className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-secondary cursor-pointer"
                        onClick={() => {
                          navigate('/contacts');
                          setQuickSearchOpen(false);
                        }}
                      >
                        <Building2 className="w-4 h-4 text-purple-600" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{company.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{company.contact_count} contact{company.contact_count !== 1 ? 's' : ''}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* No Results */}
                {searchResults.deals.length === 0 && searchResults.contacts.length === 0 && searchResults.companies.length === 0 && (
                  <div className="py-8 text-center text-sm text-muted-foreground">
                    <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No results found for "{searchQuery}"</p>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Full-Width Top Header Bar - Fixed */}
      <header 
        className="hidden lg:flex h-16 border-b border-border items-center justify-between px-6 fixed top-0 left-0 right-0 z-50"
        style={{ backgroundColor: branding?.headerColor || '#0f172a' }}
      >
        {/* Left side - Logo and Brand */}
        <div className="flex items-center gap-3">
          <img 
            src={getCurrentLogo()} 
            alt={getCurrentName()}
            className="h-10 object-contain"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <h1 className="text-xl font-heading font-bold" style={{ color: branding?.headerTextColor || '#ffffff' }}>Sales CRM</h1>
        </div>
        
        {/* Center - Quick Search Button */}
        <Button
          variant="outline"
          className="hidden md:flex items-center gap-2 text-sm w-64"
          style={{ 
            color: branding?.headerTextColor || '#ffffff',
            backgroundColor: `${branding?.headerTextColor || '#ffffff'}15`,
            borderColor: `${branding?.headerTextColor || '#ffffff'}30`
          }}
          onClick={() => setQuickSearchOpen(true)}
        >
          <Search className="w-4 h-4" />
          <span className="flex-1 text-left opacity-70">Quick search...</span>
          <kbd className="hidden lg:inline-flex h-5 items-center gap-1 rounded px-1.5 text-[10px] font-medium" style={{ backgroundColor: `${branding?.headerTextColor || '#ffffff'}15`, borderColor: `${branding?.headerTextColor || '#ffffff'}30`, border: '1px solid' }}>
            Ctrl+K
          </kbd>
        </Button>
        
        {/* Right side - Actions */}
        <div className="flex items-center gap-2">
          <StaleDealsBell position="desktop" headerTextColor={branding?.headerTextColor} />
          {/* Help Guide Popover - Inline */}
          <Popover open={helpOpen} onOpenChange={setHelpOpen}>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="sm" className="p-2 hover:bg-white/10" style={{ color: branding?.headerTextColor || '#ffffff' }} data-testid="help-guide-button">
                <HelpCircle className="w-5 h-5" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[420px] p-0 max-h-[85vh] overflow-y-auto z-[100]">
              <div className="p-4 border-b border-border bg-primary text-primary-foreground">
                <h3 className="font-heading font-bold text-lg flex items-center gap-2">
                  <BookOpen className="w-5 h-5" />
                  CRM Help Guide
                </h3>
                <p className="text-sm opacity-80 mt-1">Click any topic to learn more</p>
              </div>
              
              <div className="divide-y divide-border">
                {/* Getting Started */}
                <div 
                  className="cursor-pointer hover:bg-secondary/50 transition-colors"
                  onClick={() => setExpandedHelpSection(expandedHelpSection === 'getting-started' ? null : 'getting-started')}
                >
                  <div className="p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                        <LayoutDashboard className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      </div>
                      <span className="font-medium text-sm">Getting Started</span>
                    </div>
                    <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${expandedHelpSection === 'getting-started' ? 'rotate-90' : ''}`} />
                  </div>
                  {expandedHelpSection === 'getting-started' && (
                    <div className="px-3 pb-3 space-y-2 bg-secondary/20">
                      <div className="p-2 bg-background rounded border text-xs">
                        <strong>Login Options:</strong> Sign in with email/password, Microsoft 365, or Google. New Google users get accounts created automatically.
                      </div>
                      <div className="p-2 bg-background rounded border text-xs">
                        <strong>Dashboard:</strong> View key metrics, deal pipeline, and recent activities. Click any card to drill down.
                      </div>
                      <div className="p-2 bg-background rounded border text-xs">
                        <strong>Customers:</strong> Your central hub for accounts. Each customer has contacts, deals, and activity history.
                      </div>
                      <div className="p-2 bg-background rounded border text-xs">
                        <strong>Leads:</strong> Quick entry page - add basic info and the system creates contact + deal automatically.
                      </div>
                    </div>
                  )}
                </div>

                {/* Lead Finder */}
                <div 
                  className="cursor-pointer hover:bg-secondary/50 transition-colors"
                  onClick={() => setExpandedHelpSection(expandedHelpSection === 'lead-finder' ? null : 'lead-finder')}
                >
                  <div className="p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                        <Search className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                      </div>
                      <span className="font-medium text-sm">Lead Finder</span>
                    </div>
                    <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${expandedHelpSection === 'lead-finder' ? 'rotate-90' : ''}`} />
                  </div>
                  {expandedHelpSection === 'lead-finder' && (
                    <div className="px-3 pb-3 space-y-2 bg-secondary/20">
                      <div className="p-2 bg-background rounded border text-xs">
                        <strong>Prospect Search:</strong> Find real LinkedIn profiles via Google search. Filter by country, state/province, city, industry, and job title.
                      </div>
                      <div className="p-2 bg-background rounded border text-xs">
                        <strong>SerpAPI Integration:</strong> Configure a SerpAPI key in Settings to search real LinkedIn profiles. Get 100 free searches/month at serpapi.com.
                      </div>
                      <div className="p-2 bg-background rounded border text-xs">
                        <strong>Add as Lead:</strong> Opens a form to review/edit prospect details and select Business Unit before creating the lead.
                      </div>
                      <div className="p-2 bg-background rounded border text-xs">
                        <strong>Disqualify:</strong> Mark prospects as not suitable with a reason. Schedule follow-up callbacks if needed.
                      </div>
                    </div>
                  )}
                </div>

                {/* Pipeline */}
                <div 
                  className="cursor-pointer hover:bg-secondary/50 transition-colors"
                  onClick={() => setExpandedHelpSection(expandedHelpSection === 'pipeline' ? null : 'pipeline')}
                >
                  <div className="p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                        <DollarSign className="w-4 h-4 text-green-600 dark:text-green-400" />
                      </div>
                      <span className="font-medium text-sm">Pipeline Management</span>
                    </div>
                    <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${expandedHelpSection === 'pipeline' ? 'rotate-90' : ''}`} />
                  </div>
                  {expandedHelpSection === 'pipeline' && (
                    <div className="px-3 pb-3 space-y-2 bg-secondary/20">
                      <div className="p-2 bg-background rounded border text-xs">
                        <strong>Drag & Drop:</strong> Move deal cards between columns to update stage. Probability updates automatically.
                      </div>
                      <div className="p-2 bg-background rounded border text-xs">
                        <strong>Views:</strong> Toggle between Kanban (grid) and List (table) views using the icons in the toolbar.
                      </div>
                      <div className="p-2 bg-background rounded border text-xs">
                        <strong>Filters:</strong> Filter by date range, source, company (with search), business unit, and user.
                      </div>
                      <div className="p-2 bg-background rounded border text-xs">
                        <strong>Analytics:</strong> Click "Analytics" button to see pipeline summary - total Capex/MRR, weighted value, and top customers by value.
                      </div>
                      <div className="p-2 bg-background rounded border text-xs">
                        <strong>Disqualify:</strong> Moving to Disqualified prompts for reason and optional callback scheduling.
                      </div>
                    </div>
                  )}
                </div>

                {/* Stale Alerts */}
                <div 
                  className="cursor-pointer hover:bg-secondary/50 transition-colors"
                  onClick={() => setExpandedHelpSection(expandedHelpSection === 'stale' ? null : 'stale')}
                >
                  <div className="p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                        <Clock className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                      </div>
                      <span className="font-medium text-sm">Stale Pipeline Alerts</span>
                    </div>
                    <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${expandedHelpSection === 'stale' ? 'rotate-90' : ''}`} />
                  </div>
                  {expandedHelpSection === 'stale' && (
                    <div className="px-3 pb-3 space-y-2 bg-secondary/20">
                      <div className="p-2 bg-background rounded border text-xs">
                        <strong>Clock Icon:</strong> Shows deals not updated recently. Click to see which need attention.
                      </div>
                      <div className="p-2 bg-background rounded border text-xs">
                        <strong>Custom Thresholds:</strong> Each BU sets own timing. BU Managers adjust in Settings → Business Units.
                      </div>
                      <div className="p-2 bg-background rounded border text-xs">
                        <strong>Clear Alert:</strong> Any update (call, email, note, stage change) resets the stale timer.
                      </div>
                    </div>
                  )}
                </div>

                {/* Email & Calendar */}
                <div 
                  className="cursor-pointer hover:bg-secondary/50 transition-colors"
                  onClick={() => setExpandedHelpSection(expandedHelpSection === 'email' ? null : 'email')}
                >
                  <div className="p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                        <Mail className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                      </div>
                      <span className="font-medium text-sm">Email & Calendar</span>
                    </div>
                    <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${expandedHelpSection === 'email' ? 'rotate-90' : ''}`} />
                  </div>
                  {expandedHelpSection === 'email' && (
                    <div className="px-3 pb-3 space-y-2 bg-secondary/20">
                      <div className="p-2 bg-background rounded border text-xs">
                        <strong>Connect M365:</strong> Click profile menu → "Connect Microsoft Account" to enable email sync.
                      </div>
                      <div className="p-2 bg-background rounded border text-xs">
                        <strong>Send Email:</strong> Click email icon on contacts. Add TO/CC recipients, attachments. Logged as activity.
                      </div>
                      <div className="p-2 bg-background rounded border text-xs">
                        <strong>Auto Sync:</strong> Emails with contacts sync automatically. View history in contact Activities tab.
                      </div>
                    </div>
                  )}
                </div>

                {/* Reports */}
                <div 
                  className="cursor-pointer hover:bg-secondary/50 transition-colors"
                  onClick={() => setExpandedHelpSection(expandedHelpSection === 'reports' ? null : 'reports')}
                >
                  <div className="p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                        <BarChart3 className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                      </div>
                      <span className="font-medium text-sm">Reports & Data</span>
                    </div>
                    <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${expandedHelpSection === 'reports' ? 'rotate-90' : ''}`} />
                  </div>
                  {expandedHelpSection === 'reports' && (
                    <div className="px-3 pb-3 space-y-2 bg-secondary/20">
                      <div className="p-2 bg-background rounded border text-xs">
                        <strong>Standard Reports:</strong> Pre-built reports for pipeline, conversions, sources. Export to Excel/PDF.
                      </div>
                      <div className="p-2 bg-background rounded border text-xs">
                        <strong>Custom Builder:</strong> Create reports by selecting data type, columns, and filters. Save for reuse.
                      </div>
                      <div className="p-2 bg-background rounded border text-xs">
                        <strong>Data Import:</strong> Upload contacts/deals in bulk. Download template, fill in, upload.
                      </div>
                      <div className="p-2 bg-background rounded border text-xs">
                        <strong>Bulk Update:</strong> Export deals, modify in Excel, re-import to update multiple records at once.
                      </div>
                    </div>
                  )}
                </div>

                {/* Settings */}
                <div 
                  className="cursor-pointer hover:bg-secondary/50 transition-colors"
                  onClick={() => setExpandedHelpSection(expandedHelpSection === 'settings' ? null : 'settings')}
                >
                  <div className="p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                        <Settings className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      </div>
                      <span className="font-medium text-sm">Settings & Admin</span>
                    </div>
                    <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${expandedHelpSection === 'settings' ? 'rotate-90' : ''}`} />
                  </div>
                  {expandedHelpSection === 'settings' && (
                    <div className="px-3 pb-3 space-y-2 bg-secondary/20">
                      <div className="p-2 bg-background rounded border text-xs">
                        <strong>Global:</strong> Admin sets currency, timezone, date format, and fiscal year start for all users.
                      </div>
                      <div className="p-2 bg-background rounded border text-xs">
                        <strong>Business Units:</strong> Create BUs, upload logos, set stale alert thresholds, manage BU-specific integrations (WhatsApp, 3CX).
                      </div>
                      <div className="p-2 bg-background rounded border text-xs">
                        <strong>Users:</strong> Add users, assign roles and BUs, activate/deactivate accounts.
                      </div>
                      <div className="p-2 bg-background rounded border text-xs">
                        <strong>Integrations:</strong> Microsoft 365, Google Workspace SSO, SerpAPI for LinkedIn search, WhatsApp, 3CX - configure globally or per BU.
                      </div>
                      <div className="p-2 bg-background rounded border text-xs">
                        <strong>Branding:</strong> Customize nav/header colors, font family, default logo, and login page background image.
                      </div>
                    </div>
                  )}
                </div>

                {/* Keyboard Shortcuts */}
                <div 
                  className="cursor-pointer hover:bg-secondary/50 transition-colors"
                  onClick={() => setExpandedHelpSection(expandedHelpSection === 'shortcuts' ? null : 'shortcuts')}
                >
                  <div className="p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-900/30 flex items-center justify-center">
                        <Keyboard className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      </div>
                      <span className="font-medium text-sm">Keyboard Shortcuts</span>
                    </div>
                    <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${expandedHelpSection === 'shortcuts' ? 'rotate-90' : ''}`} />
                  </div>
                  {expandedHelpSection === 'shortcuts' && (
                    <div className="px-3 pb-3 space-y-2 bg-secondary/20">
                      <div className="p-2 bg-background rounded border text-xs flex items-center justify-between">
                        <span><strong>Quick Search</strong> - Search deals, contacts, customers</span>
                        <kbd className="px-2 py-1 bg-muted rounded text-[10px] font-mono">Ctrl+K</kbd>
                      </div>
                      <div className="p-2 bg-background rounded border text-xs flex items-center justify-between">
                        <span><strong>Toggle Sidebar</strong> - Collapse/expand navigation</span>
                        <kbd className="px-2 py-1 bg-muted rounded text-[10px] font-mono">Ctrl+B</kbd>
                      </div>
                      <div className="p-2 bg-background rounded border text-xs flex items-center justify-between">
                        <span><strong>Help Guide</strong> - Open this help guide</span>
                        <kbd className="px-2 py-1 bg-muted rounded text-[10px] font-mono">Ctrl+/</kbd>
                      </div>
                      <div className="p-2 bg-background rounded border text-xs flex items-center justify-between">
                        <span><strong>Close Modal</strong> - Close any open popup</span>
                        <kbd className="px-2 py-1 bg-muted rounded text-[10px] font-mono">Esc</kbd>
                      </div>
                      <div className="p-2 bg-muted/50 rounded border text-xs text-muted-foreground">
                        <em>Tip: On Mac, use Cmd instead of Ctrl</em>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-3 border-t border-border bg-secondary/30">
                <p className="text-xs text-muted-foreground flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Need more help? Contact your BU Manager or system administrator.
                </p>
              </div>
            </PopoverContent>
          </Popover>
          <NotificationBell textColor={branding?.headerTextColor} />
          <ThemeToggle textColor={branding?.headerTextColor} />
          <UserProfileDropdown textColor={branding?.headerTextColor} />
        </div>
      </header>

      {/* Desktop Sidebar - Fixed, below header, collapsible */}
      <aside 
        className={`hidden lg:flex ${sidebarCollapsed ? 'w-16' : 'w-64'} border-r border-border flex-col fixed top-16 left-0 bottom-0 z-40 transition-all duration-300`} 
        style={{ backgroundColor: branding?.navColor || '#1e293b' }}
      >
        <SidebarNav collapsed={sidebarCollapsed} />
      </aside>

      {/* Mobile Header - Fixed */}
      <div 
        className="lg:hidden fixed top-0 left-0 right-0 z-50 border-b border-border"
        style={{ backgroundColor: branding?.headerColor || '#0f172a' }}
      >
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <img 
              src={getCurrentLogo()} 
              alt={getCurrentName()}
              className="h-8 object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <span className="font-heading font-bold text-white">Sales CRM</span>
          </div>
          <div className="flex items-center gap-1">
            <StaleDealsBell position="mobile" />
            {/* Mobile Help Button */}
            <Button 
              variant="ghost" 
              size="sm" 
              className="p-2 hover:bg-white/10" 
              style={{ color: branding?.headerTextColor || '#ffffff' }}
              onClick={() => setHelpOpen(true)}
              data-testid="help-guide-button-mobile"
            >
              <HelpCircle className="w-5 h-5" />
            </Button>
            <NotificationBell textColor={branding?.headerTextColor} />
            <ThemeToggle textColor={branding?.headerTextColor} />
            <UserProfileDropdown textColor={branding?.headerTextColor} />
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" className="p-2 hover:bg-white/10" style={{ color: branding?.headerTextColor || '#ffffff' }} data-testid="mobile-menu-button">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0 flex flex-col">
                <MobileNavContent />
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Main Content - single Outlet shared by desktop + mobile (rendering it twice doubled every page's work) */}
      <div className={`flex-1 pt-16 bg-background h-screen overflow-hidden transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <main className="p-4 md:p-6 lg:p-8 bg-background h-full overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
